import normalizePrice from "@/utils/normalizePrice";
import { SecureShieldIcon } from "@/components/icons";
import { Button } from "aha-components";
import { Trans, useTranslation } from "react-i18next";
import { SavedPaymentMethod } from "./types";
import { s3ImageLink } from "@/utils/links";
import { TopupPreview } from "@/api/payment";

interface Props {
  balance: number;
  paymentMethod: SavedPaymentMethod | null;
  preview: TopupPreview | null;
  previewLoading: boolean;
  loading: boolean;
  onCheckout: () => void;
  variant?: "default" | "mobile-details" | "mobile-footer";
}

export default function PaymentSummary({
  balance,
  paymentMethod,
  preview,
  previewLoading,
  loading,
  onCheckout,
  variant = "default",
}: Props) {
  const { t } = useTranslation();
  const isMobileDetails = variant === "mobile-details";
  const isMobileFooter = variant === "mobile-footer";
  const hasPreview = Boolean(preview);
  const formatPreviewAmount = (value: number | undefined) =>
    hasPreview ? normalizePrice(value ?? 0) : previewLoading ? "…" : "—";

  const total = formatPreviewAmount(preview?.totalAmount);

  return (
    <aside
      className={
        isMobileFooter
          ? "border-t border-[#E7E7E7] bg-white px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_12px_rgba(0,0,0,0.05)]"
          : `flex flex-col bg-[#FAFAF7] p-6 md:h-full md:p-8${
              isMobileDetails ? "" : " h-full"
            }`
      }
    >
      {!isMobileFooter && (
        <>
          <h2 className="text-base font-semibold">
            {t("components.chargeModal.paymentSummary.title", {
              defaultValue: "Payment summary",
            })}
          </h2>
          <div className="mt-5 space-y-3 text-sm md:mt-7">
            <div className="flex justify-between"><span className="text-[#777]">{t("components.chargeModal.paymentSummary.balance", { defaultValue: "Balance" })}</span><strong>{formatPreviewAmount(preview?.amount)}</strong></div>
            <div className="flex justify-between"><span className="text-[#777]">{t("components.chargeModal.paymentSummary.processingFee", { defaultValue: "Processing fee" })}</span><strong>{formatPreviewAmount(preview?.transactionFee)}</strong></div>
            <div className="border-t border-[#E7E7E7] pt-4">
              <div className="flex items-center justify-between"><span className="text-[#777]">{t("components.chargeModal.paymentSummary.total", { defaultValue: "Total" })}</span><strong className="text-2xl">{total}</strong></div>
              <div className="mt-4 text-[#777]">
                {t("components.chargeModal.paymentSummary.balanceAfterRecharge", {
                  amount: preview
                    ? normalizePrice(balance + preview.amount)
                    : previewLoading
                      ? "…"
                      : "—",
                  defaultValue: "Balance after recharge {amount}",
                })}
              </div>
            </div>
          </div>

          <div className="mt-auto pt-8 md:pt-10">
            <ul className="space-y-2 text-sm text-[#777]">
              <li><span className="mr-2 text-[#18A66A]">✓</span>{t("components.chargeModal.refundAvailable", { defaultValue: "100% refund available anytime" })}</li>
              <li><span className="mr-2 text-[#18A66A]">✓</span>{t("components.chargeModal.priorityAccess", { defaultValue: "Priority access to top influencer matching" })}</li>
              <li><span className="mr-2 text-[#18A66A]">✓</span>{t("components.chargeModal.vipSupport", { defaultValue: "VIP support with dedicated responses" })}</li>
              <li className="flex min-h-6 items-center gap-2 leading-6">
                <SecureShieldIcon className="h-4 w-[13px] shrink-0" />
                <span className="flex min-h-6 items-center gap-2">
                  <span className="leading-6">
                    {t("components.chargeModal.paymentSummary.securedBy", {
                      defaultValue: "Secured by",
                    })}
                  </span>
                  <img
                    className="relative top-[5px] h-[22px] w-[70px] shrink-0 object-contain"
                    src={`${s3ImageLink}${paymentMethod?.provider === "airwallex" ? "airwallex-active.png" : "airwallex-default.png"}`}
                    alt="Airwallex"
                  />
                  <img
                    className="relative top-[5px] h-[22px] w-[38px] shrink-0 object-contain"
                    src={`${s3ImageLink}${paymentMethod?.provider === "stripe" ? "stripe-active.png" : "stirpe-default.png"}`}
                    alt="Stripe"
                  />
                </span>
              </li>
            </ul>
          </div>
        </>
      )}

      {!isMobileDetails && (
        <div className={isMobileFooter ? "" : "mt-4"}>
          {isMobileFooter && (
            <div className="mb-3 flex items-baseline justify-between">
              <span className="text-sm text-[#777]">
                {t("components.chargeModal.paymentSummary.total", {
                  defaultValue: "Total",
                })}
              </span>
              <strong className="text-2xl">{total}</strong>
            </div>
          )}
        <Button
          type="primary"
          size="xl"
          loading={loading}
          disabled={loading}
          onClick={onCheckout}
          className="mt-4 w-full"
        >
          {t("components.chargeModal.paymentSummary.checkout", {
            defaultValue: "Checkout",
          })}
        </Button>
        <p className="mt-4 text-[11px] leading-4 text-[#555]">
          <Trans
            i18nKey="components.chargeModal.paymentSummary.agreement"
            defaults="By proceeding, you agree to our <terms>Terms of Service and Refund Policy</terms>. For deposit details, please refer to <guide>Top-Up & Campaign Budget Guide</guide>."
            components={{
              terms: <a className="text-[#315EFB]" href="https://www.ahacreator.com/terms-of-service" target="_blank" rel="noreferrer" />,
              guide: <a className="text-[#315EFB]" href="https://screeching-vulture-3d3.notion.site/Top-Up-Campaign-Budget-Guide-25c298a7f2908016874fc36104573bbd" target="_blank" rel="noreferrer" />,
            }}
          />
        </p>
        </div>
      )}
    </aside>
  );
}
